/**
 * «Carga de DNI» junto al CUIT/DNI del autor (KAN-463).
 *
 * La acción se dibuja en el campo propio del CUIT/DNI, pero el diálogo vive en
 * FormExtras: solo él recibe del formulario `onPatch` y `options`, que hacen
 * falta para volcar lo leído. Los dos son hermanos dentro del formulario
 * generado, así que no comparten un contexto propio (FormRecordContext es del
 * generador y no lleva funciones).
 *
 * El vínculo es el lugar en la página. El campo pide la carga con un evento
 * que sube desde la acción; FormExtras lo atiende en el elemento que lo
 * contiene a él y a los campos de su formulario (su padre: la caja del
 * formulario generado). Así cada pedido llega solo al formulario donde se
 * tocó la acción:
 *  - dos formularios abiertos a la vez están en cajas distintas;
 *  - un formulario dentro de un diálogo se dibuja en otro lugar del documento
 *    (MUI lo pone en un portal), así que su pedido no sube al de atrás;
 *  - el primero que lo atiende lo detiene.
 *
 * Si el generador moviera FormExtras fuera de esa caja, el pedido no llegaría:
 * lo cubre la prueba con los formularios generados
 * (cargaDeDniJuntoAlCuit.unit.test.tsx).
 */
import { useEffect, useRef } from "react";
import type { RefObject } from "react";

const PEDIDO = "jupiter:carga-de-dni";

/**
 * Desde el campo: pide abrir la carga del DNI de su formulario. Devuelve si
 * algún formulario la atendió.
 */
export function pedirCargaDeDni(desde: Element): boolean {
  return !desde.dispatchEvent(new CustomEvent(PEDIDO, { bubbles: true, cancelable: true }));
}

/**
 * En FormExtras: atiende los pedidos que salen de su formulario. `marca` es el
 * elemento que dibuja FormExtras; el formulario es su padre.
 */
export function useAtenderCargaDeDni(marca: RefObject<HTMLElement>, abrir: () => void): void {
  const abrirActual = useRef(abrir);
  abrirActual.current = abrir;
  useEffect(() => {
    const formulario = marca.current?.parentElement;
    if (!formulario) return undefined;
    const atender = (evento: Event) => {
      evento.preventDefault();
      evento.stopPropagation();
      abrirActual.current();
    };
    formulario.addEventListener(PEDIDO, atender);
    return () => formulario.removeEventListener(PEDIDO, atender);
  }, [marca]);
}
